'use client'

import { mapCartItemToElevarRemoveFromCartData } from '@repo/elevar/mapper'
import { CartItem } from '@repo/shopify/types'
import { Button } from '@repo/ui/components/button'
import { cn } from '@repo/ui/lib/utils'
import { FunctionComponent, useActionState, useEffect } from 'react'
import { toast } from 'sonner'
import { removeCartItem } from '../../actions/cart.actions'
import { UpdateType } from '../../providers/CartProvider'
import { useTelemetry } from '../../providers/TelemetryProvider'
import { PageDto } from '../../types/page.types'

interface Props {
  pageDto: PageDto
  item: CartItem
  optimisticUpdateAction: (merchandiseId: string, updateType: UpdateType) => void
  className?: string
}

const RemoveFromCart: FunctionComponent<Props> = ({ pageDto, item, optimisticUpdateAction, className }) => {
  const { trackElevarRemoveFromCartEvent } = useTelemetry()
  const [state, formAction, pending] = useActionState(removeCartItem, null)

  // Payload for server action
  const payload = { shopifyInstance: pageDto.shopifyInstance, merchandiseId: item.merchandise.id }
  const actionWithVariant = formAction.bind(null, payload)

  useEffect(() => {
    if (!pending && state?.status === 'error') {
      toast.error(state.message)
    }
  }, [state, pending])

  return (
    <form
      action={async () => {
        // A bit optimistic event dispatch here, but since this component will unmount after completion,
        // the state or pending variables might be null (depending on unmount timing), so we cannot rely
        // on it accurately.
        trackElevarRemoveFromCartEvent(mapCartItemToElevarRemoveFromCartData(item, pageDto.params.locale))

        optimisticUpdateAction(payload.merchandiseId, 'delete')
        actionWithVariant()
      }}
    >
      <Button variant='linkAuto' size='sm' type='submit' aria-label={pageDto.dictionary?.cart?.removefrombag || 'Remove from bag'} className={cn('underline', className)}>
        {pageDto.dictionary?.cart?.remove || 'Remove'}
      </Button>
    </form>
  )
}

export default RemoveFromCart
